import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as CardPosIcon } from '../../../../assets/icons/card-pos-icon.svg';
import { ReactComponent as FleshIcon } from '../../../../assets/icons/flash-icon.svg';
import { ReactComponent as NavigationArrowIcon } from '../../../../assets/icons/navigate-back.svg';
import { ReactComponent as ReceiptIcon } from '../../../../assets/icons/receipt-icon.svg';
import { ReactComponent as PenaltyManagementFillIcon } from '../../../../assets/icons/sidebar/penalty-management-fill.svg';
import { brandText } from '../../../../config/branding';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  setEndConsumerData,
  setSelectedEndConsumer,
} from '../../../../redux/slices/EndConsumerSlice';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomRadioButton from '../../../../shared-components/custom-radio-button/CustomRadioButton';
import TabSwitchList, {
  TabItem,
} from '../../../../shared-components/tab-switch-list/TabSwitchList';
import TablesToggle from '../../../../shared-components/tables-toggle/TablesToggle';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import endConsumerApi from '../../../../utils/api/end-consumer-v2';
import endConsumerV2Api from '../../../../utils/api/v2/end-consumer';
import SessionsCountChart from '../ChartComponents/ProfileChart/SessionsCountChart';
import SpendChart from '../ChartComponents/ProfileChart/SpendChart';
import TotalConsumptionChart from '../ChartComponents/ProfileChart/TotalConsumptionChart';
import ActiveChargingSession from '../SidePaneComponents/ActiveChargingSession/ActiveChargingSession';

import ChargeSessionCommercialList from './ChargeSessionCommercialList';
import ChargeSessionPersonalList from './ChargeSessionPersonalList';
import GuestChargingList from './GuestChargingList';
// import InvoiceList from './InvoiceList';
import InvoiceList from './InvoiceList';
import PaymentHistoryList from './PaymentHistoryList';
import ProfileConsumer from './ProfileConsumer';
import ReservationsList from './ReservationsList';
import ScheduleCompletedList from './ScheduleCompletedList';
import ScheduleUpcomingList from './ScheduleUpcomingList';
import TransactionHistoryList from './TransactionHistoryList';

interface NotificationItem {
  title: string;
  createdAt: string;
}

interface OtpItemInterface {
  createdAt: string;
  otp: string;
  otpExpiresAt: string;
  type: string;
  updatedAt: string;
  userId: string;
}

/**
 * EndConsumers Profile
 * @returns {any} - The EndConsumersProfile component
 */
function EndConsumersProfile() {
  const { t, i18n } = useTranslation('endConsumer');
  const { selectedEndConsumer, basicInformation } = useAppSelector(
    (state) => state.endConsumerType,
  );
  const dispatch = useAppDispatch();
  const { type } = useParams<{ type: string }>();
  const { userCustomId } = useParams<{ userCustomId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = useLocation();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [otps, setOtps] = useState<OtpItemInterface[]>([]);
  const [leftActiveTab, setLeftActiveTab] = useState('basicInfo');
  const [rightActiveTab, setRightActiveTab] = useState('lifetime');
  const [bottomActiveTab, setBottomActiveTab] = useState('activeSession');
  const [consumerType, setConsumerType] = useState(type || 'commercial');

  const leftContainerTabs: TabItem[] = [
    {
      label: t('basicInformation'),
      key: 'basicInfo',
      isActive: leftActiveTab === 'basicInfo',
    },
    {
      label: t('notifications'),
      key: 'notifications',
      isActive: leftActiveTab === 'notifications',
    },
    {
      label: t('otps'),
      key: 'otps',
      isActive: leftActiveTab === 'otps',
    },
  ];

  const rightContainerTabs: TabItem[] = [
    {
      label: t('lifetime'),
      key: 'lifetime',
      isActive: rightActiveTab === 'lifetime',
    },
    {
      label: t('totalConsumption'),
      key: 'totalConsumption',
      isActive: rightActiveTab === 'totalConsumption',
    },
    {
      label: t('spend'),
      key: 'spend',
      isActive: rightActiveTab === 'spend',
    },
    {
      label: t('sessionsCount'),
      key: 'sessionsCount',
      isActive: rightActiveTab === 'sessionsCount',
    },
  ];

  const statsConfig = [
    {
      key: 'energy',
      label: t('lifetimeEnergyConsumption'),
      value: ToFixedValue(basicInformation?.totalPowerConsumedInKwh, 3),
      unit: 'Kw',
      icon: <FleshIcon />,
    },
    {
      key: 'spend',
      label: t('lifetimeSpend'),
      value: ToFixedValue(basicInformation?.totalRevenue),
      unit: brandText.currency,
      icon: <CardPosIcon />,
    },
    {
      key: 'penaltyPaid',
      label: t('penaltyChargesPaid'),
      value: ToFixedValue(basicInformation?.penaltySummary?.penaltyChargePaid),
      unit: brandText.currency,
      icon: <ReceiptIcon />,
    },
    {
      key: 'penaltyCount',
      label: t('penaltyCounts'),
      value: basicInformation?.penaltySummary?.penaltyCount || 0,
      icon: <PenaltyManagementFillIcon />,
    },
  ];

  const bottomContainerTabs: TabItem[] =
    consumerType === 'commercial'
      ? [
          {
            label: t('activeSession'),
            key: 'activeSession',
            isActive: bottomActiveTab === 'activeSession',
          },
          {
            label: t('chargeSession'),
            key: 'chargeSession',
            isActive: bottomActiveTab === 'chargeSession',
          },
          {
            label: t('transactionHistory'),
            key: 'transactionHistory',
            isActive: bottomActiveTab === 'transactionHistory',
          },
          {
            label: t('paymentHistory', { defaultValue: 'Payment History' }),
            key: 'paymentHistory',
            isActive: bottomActiveTab === 'paymentHistory',
          },
          {
            label: t('invoice'),
            key: 'invoice',
            isActive: bottomActiveTab === 'invoice',
          },
          {
            label: t('reservations'),
            key: 'reservations',
            isActive: bottomActiveTab === 'reservations',
          },
          {
            label: t('guestCharging'),
            key: 'guestCharging',
            isActive: bottomActiveTab === 'guestCharging',
          },
        ]
      : [
          {
            label: t('activeSession'),
            key: 'activeSession',
            isActive: bottomActiveTab === 'activeSession',
          },
          {
            label: t('schedule'),
            key: 'schedule',
            isActive: bottomActiveTab === 'schedule',
          },
          {
            label: t('chargeSession'),
            key: 'chargeSessionPersonal',
            isActive: bottomActiveTab === 'chargeSessionPersonal',
          },
          {
            label: t('paymentHistory', { defaultValue: 'Payment History' }),
            key: 'paymentHistory',
            isActive: bottomActiveTab === 'paymentHistory',
          },
        ];

  /**
   * Switch the left column tab (Basic Info / Notifications / OTPs)
   * @param {string} key - The tab key
   */
  const handleLeftSwitchTab = (key: string) => {
    setLeftActiveTab(key);
  };

  /**
   * Switch the right column tab (Total Consumption / Spend / Sessions Count / Lifetime)
   * @param {string} key - The tab key
   */
  const handleRightSwitchTab = (key: string) => {
    setRightActiveTab(key);
  };

  /**
   * Switch the bottom container tab (Charge Session / Schedule / Reservations / Guest Charging / Transaction History)
   * @param {string} key - The tab key
   */
  const handleBottomSwitchTab = (key: string) => {
    setBottomActiveTab(key);
  };

  /**
   * Switch the consumer type
   * @param {string} name - The consumer type
   * @param {string} value - The consumer type value
   */
  const handleConsumerTypeChange = (name: string, value: string) => {
    setConsumerType(value);
  };

  /**
   * Fetching card list api
   * @param {any} consumerId - The consumerId
   */
  const handleNotificationsCard = async (consumerId: string) => {
    try {
      const response = await endConsumerApi.fetchNotifications(consumerId);

      setNotifications(response?.data || []);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: error?.response?.status || 500,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Fetching Card OTP function
   * @param {any} consumerId - The consumerId
   */
  const handleFetchOtp = async (consumerId: string) => {
    try {
      const response = await endConsumerV2Api.fetchOtps(consumerId);

      setOtps(response?.data || []);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: error?.response?.status || 500,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Navigate back to the source page or previous route dynamically
   */
  const handleNavigateBack = () => {
    if (location.state?.source) {
      navigate(location.state.source);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    // Run only for profile page
    if (!pathname.includes('/end-consumer-v2/profile')) return;

    requestAnimationFrame(() => {
      const scrollContainer = document.querySelector('.ev-content');

      if (scrollContainer instanceof HTMLElement) {
        scrollContainer.scrollTop = 0;
      } else {
        window.scrollTo(0, 0);
      }
    });
  }, []);

  useEffect(() => {
    dispatch(
      setSelectedEndConsumer({
        userCustomId,
      }),
    );
    dispatch(
      setEndConsumerData({
        name: 'endConsumerType',
        value: type || '',
      }),
    );
  }, [userCustomId, type]);

  useEffect(() => {
    if (selectedEndConsumer?.userCustomId) {
      handleNotificationsCard(selectedEndConsumer?.userCustomId);
      handleFetchOtp(selectedEndConsumer?.userCustomId);
    }
  }, [selectedEndConsumer?.userCustomId]);
  return (
    <div className="end-consumers-container" dir={i18n.dir()}>
      {/* <div className="end-consumer-profile-header">
        <div className="navigate-arrow" role="presentation" onClick={handleNavigateBack}>
          <span>
            <NavigationArrowIcon className="icon" />
          </span>
          <div className="navigate-background" />
        </div>
      </div> */}

      <div className="ec-profile-top-grid">
        {/* Left Column: Basic Info / Notifications / OTPs */}
        <div className="ec-profile-left-column">
          <div className="left-column-header">
            <div className="end-consumer-profile-header">
              <div className="navigate-arrow" role="presentation" onClick={handleNavigateBack}>
                <span>
                  <NavigationArrowIcon className="icon" />
                </span>
                <div className="navigate-background" />
              </div>
            </div>
            <div className="tab-switch-container">
              <TabSwitchList tabs={leftContainerTabs} onSwitchTab={handleLeftSwitchTab} />
            </div>
          </div>

          <div className="left-tab-content">
            {leftActiveTab === 'basicInfo' && <ProfileConsumer />}
            {leftActiveTab === 'notifications' && (
              <div className="menu-tab-container">
                <div className="menu-tab-body">
                  {notifications?.length > 0 ? (
                    notifications.map((item: NotificationItem) => (
                      <div key={`${item.title}-${item.createdAt}`} className="menu-tab-row">
                        <span className="title">{item.title}</span>
                        <span className="time">{ConvertUTCtoDateTime(item.createdAt)}</span>
                      </div>
                    ))
                  ) : (
                    <p className="no-data">{t('noNotificationsFound')}</p>
                  )}
                </div>
              </div>
            )}
            {leftActiveTab === 'otps' && (
              <div className="menu-tab-container">
                <div className="menu-tab-body">
                  {otps?.length > 0 ? (
                    otps.map((item: OtpItemInterface) => (
                      <div key={`${item.otp}-${item.createdAt}`} className="menu-tab-row">
                        <span className="title">{item.otp}</span>
                        <span className="otp-type">{item.type}</span>
                        <span className="time">{ConvertUTCtoDateTime(item.createdAt)}</span>
                      </div>
                    ))
                  ) : (
                    <p className="no-data">{t('noOtpsFound')}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Total Consumption / Spend / Sessions Count */}
        <div className="ec-profile-right-column">
          <div className="tab-switch-container">
            <TabSwitchList tabs={rightContainerTabs} onSwitchTab={handleRightSwitchTab} />
          </div>

          <div className="right-tab-content">
            {rightActiveTab === 'totalConsumption' && <TotalConsumptionChart />}
            {rightActiveTab === 'spend' && <SpendChart />}
            {rightActiveTab === 'sessionsCount' && <SessionsCountChart />}
            {rightActiveTab === 'lifetime' && (
              <div className="stat-card-content">
                {statsConfig.map((item) => (
                  <div key={item.key} className="stat-card">
                    <div className="icon-wrapper">{item.icon}</div>

                    <div className="content">
                      <p className="label">{item.label}</p>

                      <p className="value">
                        {item.value}
                        {item.unit && <span className="unit"> {item.unit}</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bottom-line" />

      <div className="table-list">
        <div className="consumer-type-toggles-row">
          <CustomRadioButton
            selected={consumerType === 'commercial'}
            label={t('commercial')}
            value="commercial"
            name="consumerType"
            onChangeFunc={handleConsumerTypeChange}
          />
          <CustomRadioButton
            selected={consumerType === 'personal'}
            label={t('personal')}
            value="personal"
            name="consumerType"
            onChangeFunc={handleConsumerTypeChange}
          />
        </div>
        <TabSwitchList tabs={bottomContainerTabs} onSwitchTab={handleBottomSwitchTab} />
        {/* Renders ONLY when bottomActiveTab is 'chargeSession' (Commercial) */}
        {bottomActiveTab === 'chargeSession' && (
          <div className="table-data-list">
            <ChargeSessionCommercialList />
          </div>
        )}
        {/* Renders ONLY when bottomActiveTab is 'chargeSessionPersonal' (Personal) */}
        {bottomActiveTab === 'chargeSessionPersonal' && (
          <div className="table-data-list">
            <ChargeSessionPersonalList />
          </div>
        )}
        {bottomActiveTab === 'activeSession' && (
          <div className="table-data-list">
            <ActiveChargingSession isSplitView />
          </div>
        )}
        {bottomActiveTab === 'schedule' && (
          <div className="table-data-list">
            <TablesToggle
              tabs={[
                { label: t('upcoming'), content: <ScheduleUpcomingList /> },
                { label: t('completed'), content: <ScheduleCompletedList /> },
              ]}
              defaultActive={t('upcoming')}
            />
          </div>
        )}
        {bottomActiveTab === 'invoice' && (
          <div className="table-data-list">
            <InvoiceList />
          </div>
        )}
        {bottomActiveTab === 'reservations' && (
          <div className="table-data-list">
            <ReservationsList />
          </div>
        )}
        
        {bottomActiveTab === 'guestCharging' && (
          <div className="table-data-list">
            <GuestChargingList />
          </div>
        )}
        {bottomActiveTab === 'transactionHistory' && (
          <div className="table-data-list">
            <TransactionHistoryList />
          </div>
        )}
        {bottomActiveTab === 'paymentHistory' && (
          <div className="table-data-list">
            <PaymentHistoryList />
          </div>
        )}
      </div>
    </div>
  );
}

export default EndConsumersProfile;
